import { forwardRef, InputHTMLAttributes, useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/utils/cn";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  labelClassName?: string;
  required?: boolean;
  optional?: boolean;
  hint?: string;
  error?: string;
  id: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { label, labelClassName, id, required, optional, hint, error, className = "", type, ...props },
    ref,
  ) => {
    // Password fields get a show/hide eye toggle. We swap the rendered `type`
    // between "password" and "text" locally; the caller still passes type="password".
    const isPassword = type === "password";
    const [showPassword, setShowPassword] = useState(false);
    const resolvedType = isPassword && showPassword ? "text" : type;

    return (
      <div className="flex flex-col gap-2">
        {label && (
          <label htmlFor={id} className={labelClassName ?? "label"}>
            {label}
            {required && <span className="ml-1 text-required-star">*</span>}
            {optional && (
              <span className="ml-2 text-12 font-normal text-theme-text-secondary">(Optional)</span>
            )}
          </label>
        )}
        <div className="relative">
          <input
            ref={ref}
            id={id}
            type={resolvedType}
            aria-invalid={error ? true : undefined}
            className={cn(
              "input form-style-light",
              // Leave room for the eye button so long values don't run under it.
              isPassword && "pr-11",
              error && "border-error-500 focus:border-error-500 focus:ring-error-500",
              props.readOnly && "cursor-default opacity-60",
              className,
            )}
            {...props}
          />
          {isPassword && (
            <button
              type="button"
              // Keep it off the tab order — it's a convenience, not a form field —
              // and stop it from stealing focus/blur from the input on click.
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              aria-pressed={showPassword}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-theme-text-secondary hover:text-theme-text-heading"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Eye className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          )}
        </div>
        {hint && !error && <p className="text-12 text-theme-text-secondary">{hint}</p>}
        {error && (
          <p data-field-error className="text-12 text-error-500">
            {error}
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";
